import { useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Wallet } from 'lucide-react'

import type { BillingPeriod } from '@/api/types'
import { payrollApi } from '@/api/endpoints'
import { useAuth } from '@/auth/AuthContext'
import { isAdmin } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Card } from '@/components/ui/Card'
import { cn } from '@/lib/cn'
import { PayrollReportPanel } from './PayrollReportPanel'
import { TeacherSummaryPanel } from './TeacherSummaryPanel'

type Zakladka = 'moje' | 'raport'

export function BillingPage() {
  const { user } = useAuth()
  const administracja = isAdmin(user)

  const [zakladka, setZakladka] = useState<Zakladka>(administracja ? 'raport' : 'moje')

  const periodsQuery = useQuery({
    queryKey: ['payroll', 'periods'],
    queryFn: () => payrollApi.periods(),
    placeholderData: keepPreviousData,
    // Granice semestrów nie zmieniają się w trakcie sesji.
    staleTime: 60 * 60 * 1000,
  })

  const okresy = useMemo<BillingPeriod[]>(() => periodsQuery.data ?? [], [periodsQuery.data])

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rozliczenia"
        description={
          administracja
            ? 'Raport do wypłat i zestawienia okresowe nauczycieli.'
            : 'Twoje lekcje, wynagrodzenie i zamykanie miesiąca.'
        }
      />

      {administracja && (
        <Card>
          <div
            role="tablist"
            aria-label="Widok rozliczeń"
            className="flex gap-1 overflow-x-auto p-2"
          >
            {(
              [
                { value: 'raport', label: 'Raport do wypłat' },
                { value: 'moje', label: 'Zestawienie nauczyciela' },
              ] as const
            ).map((item) => (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={zakladka === item.value}
                onClick={() => setZakladka(item.value)}
                className={cn(
                  'rounded-lg px-4 py-2 text-sm font-semibold transition',
                  zakladka === item.value
                    ? 'bg-primary text-on-primary shadow-raised'
                    : 'text-on-surface-variant hover:bg-surface-container-low hover:text-primary',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        </Card>
      )}

      {zakladka === 'raport' && administracja ? (
        <PayrollReportPanel />
      ) : (
        <TeacherSummaryPanel okresy={okresy} isAdmin={administracja} />
      )}

      {!administracja && okresy.length === 0 && periodsQuery.isError && (
        <Card>
          <p className="flex items-center gap-2 p-6 text-sm text-on-surface-variant">
            <Wallet size={18} />
            Nie udało się pobrać okresów rozliczeniowych. Odśwież stronę.
          </p>
        </Card>
      )}
    </div>
  )
}
